import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles, Search, Plus, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageLoader, PageHeader, Modal, Btn } from "@/components/common";
import { SCHOOL_COLORS } from "@/lib/dnd";

export default function Spells() {
  const [search, setSearch] = useState("");
  const [level, setLevel] = useState(-1);
  const [expanded, setExpanded] = useState(null);
  const [addFor, setAddFor] = useState(null); // spell pending add

  const { data: spells, isLoading } = useQuery({
    queryKey: ["spells", search, level],
    queryFn: async () => (await api.get("/spells", { params: { search, level } })).data,
  });

  return (
    <div>
      <PageHeader title="Spellbook" subtitle="SRD spells — browse and add to your heroes" icon={Sparkles} />

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input data-testid="spell-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search spells…" className="input-dark w-full rounded-xl pl-11 pr-4 py-2.5 text-sm" />
        </div>
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {[-1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((l) => (
            <button key={l} data-testid={`level-filter-${l}`} onClick={() => setLevel(l)} className={`px-3.5 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition ${level === l ? "btn-violet" : "glass glass-hover text-zinc-400"}`}>
              {l === -1 ? "All" : l === 0 ? "Cantrips" : `Lvl ${l}`}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? <PageLoader /> : (
        <div className="grid md:grid-cols-2 gap-3">
          {spells?.map((sp) => (
            <div key={sp.id} className="glass rounded-2xl overflow-hidden" data-testid={`spell-${sp.id}`}>
              <div className="p-4 flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center font-display font-bold shrink-0" style={{ background: `${SCHOOL_COLORS[sp.school]}22`, color: SCHOOL_COLORS[sp.school] }}>
                  {sp.level === 0 ? "C" : sp.level}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-white">{sp.name}</div>
                  <div className="text-xs text-zinc-500">{sp.school} · {sp.level === 0 ? "Cantrip" : `Level ${sp.level}`} · {sp.casting_time}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setExpanded(expanded === sp.id ? null : sp.id)} className="p-2 rounded-lg glass-hover text-zinc-400"><ChevronDown size={16} className={`transition ${expanded === sp.id ? "rotate-180" : ""}`} /></button>
                  <button onClick={() => setAddFor(sp)} data-testid={`add-spell-${sp.id}`} className="p-2 rounded-lg bg-violet-500/15 text-violet-300 hover:bg-violet-500/25"><Plus size={16} /></button>
                </div>
              </div>
              {expanded === sp.id && (
                <div className="px-4 pb-4 -mt-1">
                  <div className="grid grid-cols-2 gap-2 text-xs text-zinc-500 mb-2">
                    <span>Range: <span className="text-zinc-300">{sp.range}</span></span>
                    <span>Duration: <span className="text-zinc-300">{sp.duration}</span></span>
                    <span>Components: <span className="text-zinc-300">{sp.components}</span></span>
                    <span>Classes: <span className="text-zinc-300">{sp.classes?.join(", ")}</span></span>
                  </div>
                  <p className="text-sm text-zinc-300 leading-relaxed">{sp.description}</p>
                </div>
              )}
            </div>
          ))}
          {spells?.length === 0 && <p className="text-zinc-600 col-span-2 text-center py-12">No spells match your search.</p>}
        </div>
      )}

      <AddToCharacter spell={addFor} onClose={() => setAddFor(null)} />
    </div>
  );
}

function AddToCharacter({ spell, onClose }) {
  const qc = useQueryClient();
  const { data: chars } = useQuery({ queryKey: ["characters"], queryFn: async () => (await api.get("/characters")).data, enabled: !!spell });

  const add = async (c) => {
    try {
      const fresh = (await api.get(`/characters/${c.id}`)).data;
      const entry = { id: crypto.randomUUID(), name: spell.name, level: spell.level, school: spell.school, description: spell.description, prepared: true };
      await api.put(`/characters/${c.id}`, { spells: [...(fresh.spells || []), entry] });
      qc.invalidateQueries({ queryKey: ["character", c.id] });
      qc.invalidateQueries({ queryKey: ["characters"] });
      toast.success(`${spell.name} added to ${c.name}`);
      onClose();
    } catch { toast.error("Failed to add spell"); }
  };

  return (
    <Modal open={!!spell} onClose={onClose} title={spell ? `Add "${spell.name}" to…` : ""}>
      {chars?.length ? (
        <div className="space-y-2">
          {chars.map((c) => (
            <button key={c.id} onClick={() => add(c)} data-testid={`add-spell-to-${c.id}`} className="w-full glass glass-hover rounded-2xl p-3 flex items-center gap-3 text-left">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-violet-900 flex items-center justify-center font-display font-bold">{c.name?.[0]?.toUpperCase()}</div>
              <div className="flex-1"><div className="font-semibold text-white">{c.name}</div><div className="text-xs text-zinc-500">Lvl {c.level} {c.class}</div></div>
              <Plus size={16} className="text-violet-300" />
            </button>
          ))}
        </div>
      ) : <p className="text-sm text-zinc-500 text-center py-6">Create a character first.</p>}
    </Modal>
  );
}
